import React, { useState, useEffect, useMemo } from 'react';
import { useLazyQuery, useQuery, useSubscription } from '@apollo/client';

import { BaseReact } from '../../base.model';
import { ProfileQL } from '../../models/Profile';
import { StripePricingPlan } from '../../models/Stripe';
import { OpenSpeiPaymentRecord, PaymentRecordEntry } from '../../models/Payments';
import { BillingAccountSubscriptionUpdatedPublishEvent, BillingAccountUpdatedPublishEvent, PaymentIntentSpeiRequestedInstructions } from '../../models/BillingAccount';
import moment from '../../services/unifiedMoment';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import SectionTitleBar from '../components/titlebar';
import AccountPayment from './PaymentDialogs/Account.payment';
import AccountPaymentChoice from './PaymentDialogs/Account.payment.choice';
import { graphqlSchema } from '../../services/graphql.schema';
import { numberToCurrencyString } from '../../services/formatting';
import SelectCard from '../Forms/Select.cardForm';
import AccountInfo from './AccountInfo/AccountInfo';
import AccountPlans from './AccountPlans/AccountPlans';
import AccountPlansModal from './AccountPlans/AccountPlansModal';
import { downloadUrls } from '../../services/fiscalpopApi';
import './Account.scss';
import { onlyPaymentPlansFeatureFlag } from './helpers/onlyPlansFeatureFlag';

interface AccountProps extends BaseReact {
    currentUser: ProfileQL;
}

function Account({ history, location, currentUser }: AccountProps) {
    const { recallCurrentUser } = useCurrentUser();
    const [addPayment, setAddPayment] = useState(false);
    const [showPaymentChoice, setShowPaymentChoice] = useState(false);
    const [showPlansModal, setShowPlansModal] = useState(false);
    // List Orders
    const [paymentOrders, setPaymentOrders] = useState<PaymentRecordEntry[]>([]);
    const [openSpeiPayment, setOpenSpeiPayment] = useState<OpenSpeiPaymentRecord>(null);
    const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
    // Prices
    const [pricingPlans, setPricingPlans] = useState<StripePricingPlan[]>([]);

    // Transitioning out of "Recarga" in favour of Payment plans only
    const ONLY_PAYMENT_PLANS_FF = useMemo(() => {
        return onlyPaymentPlansFeatureFlag(currentUser);
    }, [currentUser]);

    useQuery(graphqlSchema.STRIPE.ORDERS.getPaymentOrderRecords, {
        onCompleted: ({ getPaymentOrderRecords }: { getPaymentOrderRecords: PaymentRecordEntry[] }) => {
            console.log('Payment orders gotten: ', getPaymentOrderRecords);
            setPaymentOrders(getPaymentOrderRecords);
            const lastYearInOrder = getPaymentOrderRecords[0] ? new Date(getPaymentOrderRecords[0].createdAt).getFullYear() : new Date().getFullYear();
            setYearFilter(lastYearInOrder);
        }
    });

    useQuery(graphqlSchema.STRIPE.ORDERS.getOpenSpeiPayment, {
        onCompleted: ({ getOpenSpeiPayment }: { getOpenSpeiPayment: OpenSpeiPaymentRecord }) => {
            console.log('Open SPEI payments gotten: ', getOpenSpeiPayment);
            setOpenSpeiPayment(getOpenSpeiPayment);
        }
    });

    useQuery(graphqlSchema.STRIPE.PRICES.getPricingPlans, {
        onCompleted: ({ getPricingPlans }: { getPricingPlans: StripePricingPlan[] }) => {
            console.log('Pricing plans gotten: ', getPricingPlans);
            setPricingPlans(getPricingPlans);
        }
    });

    const [getPaymentOrders] = useLazyQuery(graphqlSchema.STRIPE.ORDERS.getPaymentOrderRecords, {
        onCompleted: ({ getPaymentOrderRecords }: { getPaymentOrderRecords: PaymentRecordEntry[] }) => {
            console.log('Payment orders gotten: ', getPaymentOrderRecords);
            setPaymentOrders(getPaymentOrderRecords)
        },
        fetchPolicy: 'network-only'
    });

    const [getOpenSpeiPayment] = useLazyQuery(graphqlSchema.STRIPE.ORDERS.getOpenSpeiPayment, {
        onCompleted: ({ getOpenSpeiPayment }: { getOpenSpeiPayment: OpenSpeiPaymentRecord }) => {
            console.log('Open SPEI payments gotten: ', getOpenSpeiPayment);
            setOpenSpeiPayment(getOpenSpeiPayment);
        },
        fetchPolicy: 'network-only'
    });

    useSubscription<{ billingAccountSubscriptionUpdated: BillingAccountSubscriptionUpdatedPublishEvent }>(graphqlSchema.FISCALPOP.PAYMENTS.billingAccountSubscriptionUpdated, {
        onSubscriptionData: ({ subscriptionData }) => {
            console.log('Billing Account Plan Updated [Account View]: ', subscriptionData);
            // Updated Plan is listened to by Toaster, which invokes Graphql user Recall
        }
    });

    useSubscription<{ billingAccountUpdated: BillingAccountUpdatedPublishEvent }>(graphqlSchema.FISCALPOP.PAYMENTS.billingAccountUpdated, {
        onSubscriptionData: ({ subscriptionData }) => {
            console.log('Billing Account Updated Subscription [Account View]: ', subscriptionData);
            // A movement was reported, update list 
            getPaymentOrders();
            // Fetch SPEI instructions, if any
            getOpenSpeiPayment();
        }
    });

    useSubscription<{ speiPaymentRequested: PaymentIntentSpeiRequestedInstructions }>(graphqlSchema.FISCALPOP.PAYMENTS.speiPaymentRequested, {
        onSubscriptionData: ({ subscriptionData }) => {
            console.log('SPEI Requested Instructions: ', subscriptionData);
            // Actively request displaying SPEI payment
            getOpenSpeiPayment();
        }
    });

    const isClusterAccount = useMemo(() => {
        return !!(currentUser?.billing?.isClusterType && currentUser?.billing?.clusterName);
    }, [currentUser]);

    const paymentOrdersByYear = useMemo(() => {
        return paymentOrders.filter(po => {
            const poYear = new Date(po.createdAt).getFullYear();
            return poYear === yearFilter;
        });
    }, [paymentOrders, yearFilter]);

    const yearFilterOptions = useMemo(() => {
        const yearsSet = new Set<number>();
        paymentOrders.forEach(po => {
            const poYear = new Date(po.createdAt).getFullYear();
            yearsSet.add(poYear);
        });
        const yearsArray = Array.from(yearsSet).sort((a, b) => b - a).slice(0, 5); // Last 5 years MAX
        return yearsArray.map(y => ({ label: y.toString(), value: y }));
    }, [paymentOrders]);

    const cheapestPlanCfdiCostPesos = useMemo(() => {
        if (!pricingPlans?.length) return null;
        const minCents = pricingPlans.filter(p => !p.isMeteredBilling).reduce<number | null>((acc, plan) => {
            if (typeof plan.cfdiMinAmountCents !== 'number') return acc;
            if (acc === null) return plan.cfdiMinAmountCents;
            return Math.min(acc, plan.cfdiMinAmountCents);
        }, null);
        return minCents === null ? null : minCents / 100;
    }, [pricingPlans]);

    const closePaymentModal = (paymentResponse: { success: boolean } = null) => {
        setAddPayment(false);
        if (!paymentResponse) {
            return;
        }
        console.log('Payment response onClose: ', paymentResponse);
    }

    const openAddBalanceFlow = () => {
        if (isClusterAccount) {
            return;
        }
        if (ONLY_PAYMENT_PLANS_FF) {
            setShowPlansModal(true);
        } else {
            setShowPaymentChoice(true);
        }
    }

    const handleChoosePlan = () => {
        setShowPaymentChoice(false);
        setShowPlansModal(true);
    }

    const handleChooseRecarga = () => {
        setShowPaymentChoice(false);
        setAddPayment(true);
    }

    useEffect(() => {
        recallCurrentUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getOrderState = (ord: PaymentRecordEntry) => {
        if (ord.isPaid) {
            return <p className="_state good">Pagado</p>;
        }
        else if (ord.isRefunded) {
            return <p className="_state bad">Devuelto</p>;
        }
        else {
            return <p className="_state meh">No pagado</p>;
        }
    }
    const getOrderType = (ord: PaymentRecordEntry) => {
        if (ord.type === 'card') {
            return 'Tarjeta';
        } else if (ord.type === 'spei') {
            return 'SPEI'
        } else if (ord.type === 'MercadoPago') {
            return 'Mercado Pago'
        } else {
            return 'Otro';
        }
    }

    const renderOpenSpeiNotice = () => {
        // Cluster accounts do not receive data
        if (!openSpeiPayment) return null;
        if (isClusterAccount) return null;
        if (currentUser.billing?.hasStripeSubscription) return null; // Only show for non-subscribed users, as subscribed users have active balance and do not need to be notified of pending payments

        return (
            <div className="openSpeiNotice card">
                <div className='noticeText'>
                    <h4>Pago SPEI pendiente</h4>
                    <p className='_onDesktop'>
                        Completa tu transferencia SPEI por <b>$ {numberToCurrencyString(openSpeiPayment.amountCentsRemaining / 100)} MXN</b>. <br />
                        Revisa las instrucciones de pago y <b>recuerda agregar la referencia a tu transferencia bancaria.</b>
                    </p>
                    <p className='_onMobile'>
                        Completa tu transferencia SPEI por <b>$ {numberToCurrencyString(openSpeiPayment.amountCentsRemaining / 100)} MXN</b>. <br />
                        <b>No olvides agregar la referencia a tu pago.</b>
                    </p>
                </div>
                <div className='noticeLink'>
                    <a href={openSpeiPayment.instructionsUrl} target='_blank' rel="noopener noreferrer">
                        Ver instrucciones
                    </a>
                </div>
            </div>
        );
    }

    const renderPaymentHistory = () => {
        return (
            <div className="movementList card">
                <div className="titleFilter">
                    <h4>Historial de pagos</h4>
                    <div className="filterControls">
                        <SelectCard
                            label='Año'
                            value={yearFilter}
                            onChange={setYearFilter}
                            options={yearFilterOptions}
                        />
                    </div>
                </div>
                <div className="paymentEntry header">
                    <p>Fecha pago</p>
                    <p className='_tipoPago'>Tipo de pago</p>
                    <p>Estado de Pago</p>
                    <p className='_cantidad'>Cantidad</p>
                    <p className='_download'>Descargar Factura</p>
                </div>
                {
                    paymentOrdersByYear.map((ord, i) => {

                        return (
                            <div className="paymentEntry" key={i}>
                                <div className='_date'>
                                    <p>{moment(ord.createdAt).format('D MMM, YYYY')}</p>
                                    <p className="_small">{moment(ord.updatedAt || ord.createdAt).format('hh:mm A')}</p>
                                </div>
                                <p className='_tipoPago'>{getOrderType(ord)}</p>
                                {getOrderState(ord)}
                                <p>$ {numberToCurrencyString(ord.amountCents / 100)}</p>
                                {
                                    ord.invoiceUUID && ord.isPaid ?
                                        <div className="splitDownlaods">
                                            <a rel="noopener noreferrer" href={downloadUrls.paymentPdf(ord.invoiceUUID)} download={true}>
                                                PDF
                                            </a>
                                            <a rel="noopener noreferrer" href={downloadUrls.paymentXml(ord.invoiceUUID)} download={true}>
                                                XML
                                            </a>
                                        </div>
                                        :
                                        <p className="uuid">
                                            {ord.isPaid ? 'No disponible' : ''}
                                        </p>
                                }
                            </div>
                        )
                    })
                }
            </div>
        )
    }

    const renderIsClusterNotice = () => {
        return (
            <div className="movementList card">
                <h4>Historial de pagos</h4>
                <div className="isClusterNotice">
                    <h3>Historial de pagos no disponible para cuentas manejadas externamente</h3>
                </div>
            </div>
        )
    }

    return (
        <div id="AccountWithStripe">
            <SectionTitleBar currentUser={currentUser} title="Cuenta y pagos" />
            <div className="accountContent">
                <div className='accountAndPlans'>
                    <AccountInfo
                        pricingPlans={pricingPlans}
                        isClusterAccount={isClusterAccount}
                        billingAccount={currentUser.billing}
                        onAddBalanceClick={openAddBalanceFlow}
                        onlyPaymentPlans={ONLY_PAYMENT_PLANS_FF}
                    />
                    <AccountPlans
                        pricingPlans={pricingPlans}
                        isClusterAccount={isClusterAccount}
                        billingAccount={currentUser.billing}
                    />
                </div>
                <div className="movementsSection">
                    {renderOpenSpeiNotice()}
                    {
                        isClusterAccount ?
                            renderIsClusterNotice()
                            :
                            renderPaymentHistory()
                    }
                </div>
            </div>
            <AccountPayment isOpen={addPayment} onRequestClose={closePaymentModal} />
            <AccountPaymentChoice
                isOpen={showPaymentChoice}
                onRequestClose={() => setShowPaymentChoice(false)}
                onChoosePlan={handleChoosePlan}
                onChooseRecarga={handleChooseRecarga}
                cheapestPlanCfdiCostPesos={cheapestPlanCfdiCostPesos}
            />
            <AccountPlansModal
                isOpen={showPlansModal}
                onRequestClose={() => setShowPlansModal(false)}
                pricingPlans={pricingPlans}
                isClusterAccount={isClusterAccount}
                billingAccount={currentUser.billing}
            />
        </div>
    )
}

export default Account;